import React from 'react';
import { Select, Span } from 'components';
import {
	getContractFrequencyPresentation,
	getContractFrequencyStatusLabel,
} from './contractFrequencyReview.model';

const UNRESOLVED_FREQUENCY = 'Frecuencia no resuelta';

const getVisibleCandidateToken = value =>
	String(value ?? '')
		.normalize('NFKC')
		.trim()
		.replace(/\s+/g, ' ');

const getCandidateTokenKey = value =>
	getVisibleCandidateToken(value).toLowerCase();

const getCandidateOptions = candidates => {
	const seenFrequencyIds = new Set();
	const selectableCandidates = candidates.filter(candidate => {
		if (seenFrequencyIds.has(candidate.frequencyId)) return false;
		seenFrequencyIds.add(candidate.frequencyId);
		return true;
	});
	const candidatesByName = selectableCandidates.reduce((result, candidate) => {
		const nameKey = getCandidateTokenKey(candidate.name);
		if (!result.has(nameKey)) result.set(nameKey, []);
		result.get(nameKey).push(candidate);
		return result;
	}, new Map());

	const initialOptions = selectableCandidates.map(candidate => {
		const visibleName = getVisibleCandidateToken(candidate.name);
		const sameNameCandidates =
			candidatesByName.get(getCandidateTokenKey(candidate.name)) || [];
		if (sameNameCandidates.length < 2)
			return { label: visibleName, value: candidate.frequencyId };

		const visibleCode = getVisibleCandidateToken(candidate.code);
		const codeKey = getCandidateTokenKey(candidate.code);
		const hasUniqueCode =
			Boolean(codeKey) &&
			sameNameCandidates.filter(
				option => getCandidateTokenKey(option.code) === codeKey,
			).length === 1;
		return {
			label: hasUniqueCode
				? `${visibleName} · ${visibleCode}`
				: `${visibleName} · ID ${candidate.frequencyId}`,
			value: candidate.frequencyId,
		};
	});
	const optionsRequiringId = new Set();
	while (true) {
		const optionIndexesByLabel = initialOptions.reduce(
			(result, option, index) => {
				const label = optionsRequiringId.has(index)
					? `${option.label} · ID ${option.value}`
					: option.label;
				const labelKey = getCandidateTokenKey(label);
				if (!result.has(labelKey)) result.set(labelKey, []);
				result.get(labelKey).push(index);
				return result;
			},
			new Map(),
		);
		let foundNewCollision = false;
		optionIndexesByLabel.forEach(indexes => {
			if (indexes.length < 2) return;
			indexes.forEach(index => {
				if (optionsRequiringId.has(index)) return;
				optionsRequiringId.add(index);
				foundNewCollision = true;
			});
		});
		if (!foundNewCollision) break;
	}

	return initialOptions.map((option, index) =>
		optionsRequiringId.has(index)
			? { ...option, label: `${option.label} · ID ${option.value}` }
			: option,
	);
};

const ContractFrequencyId = ({ rowData, field, disabled = false }) => {
	const frequency = getContractFrequencyPresentation(rowData);
	const fallbackValue =
		(field?.name ? rowData?.[field.name] : undefined) ?? rowData?.[field?.id];
	const displayOnlyFallback =
		!frequency.hasMatch ? String(fallbackValue ?? '').trim() : '';
	const canonicalLabel = frequency.canonical?.name || UNRESOLVED_FREQUENCY;

	if (!frequency.canSelect || typeof field?.onChange !== 'function') {
		return (
			<Span title={getContractFrequencyStatusLabel(frequency.status)}>
				{displayOnlyFallback || canonicalLabel}
			</Span>
		);
	}

	const options = getCandidateOptions(frequency.candidates);
	const selectedCandidate = frequency.candidates.find(
		candidate => candidate.frequencyId === frequency.canonical?.frequencyId,
	);

	return (
		<div onClick={event => event.stopPropagation()}>
			<Select
				width='100%'
				showSearch
				optionFilterProp='label'
				placeholder={canonicalLabel}
				value={selectedCandidate?.frequencyId}
				disabled={disabled}
				options={options}
				onChange={value => {
					const option = frequency.candidates.find(
						candidate => candidate.frequencyId === Number(value),
					);
					if (option) field.onChange({ field, rowData, option });
				}}
			/>
		</div>
	);
};

export default ContractFrequencyId;
